import { not } from 'ramda'
import { Subject } from 'rxjs'
import { PlainSearchParameters } from 'algoliasearch-helper'
import { InstantSearchOptions as InstantSearchConfig } from 'instantsearch.js/es/types'

import { Component, DestroyRef, Input, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { AnalyticsService } from '@app-domains/analytics/analytics.service'
import { Analytics } from '@app-domains/analytics/types/analytics.types'
import { Product } from '@app-domains/content-blocks/components/product-card/product-card.types'
import { EmptyState } from '@app-domains/empty-state/types/empty-state'
import { LocalizationService } from '@app-domains/localization/service/localization.service'
import { CategoryFragment } from '@app-graphql/schema'
import { AuthService, DebtorService, ProductSelectionService } from '@app-services'
import { AlgoliaInstantsearchComponent } from '@app-domains/algolia/components/algolia-instantsearch.component'
import { PRODUCT_LIST_FILTERS_ORDER_CONFIG, ProductListFilterEnum } from '@app-domains/product-list/types'
import { AlgoliaFilter } from '@app-lib/algolia.lib'

@Component({
    selector: 'app-product-list',
    templateUrl: './product-list.component.html',
    styleUrls: ['./product-list.component.scss'],
    providers: [ProductSelectionService],
})
export class ProductListComponent implements OnInit {

    @Input()
    public instantSearchConfig: InstantSearchConfig

    @Input()
    public searchParameters: PlainSearchParameters

    @Input()
    public clearRefinementsCommands$: Subject<void>

    @Input()
    public emptyState: EmptyState

    @Input()
    public hideBrandFilter: boolean = false

    @Input()
    public category?: CategoryFragment

    @Input()
    public listType: Analytics.ListType

    @Input()
    public filtersOrder?: ProductListFilterEnum[] = PRODUCT_LIST_FILTERS_ORDER_CONFIG

    @ViewChild('instantsearchInstance', { static: true })
    public instantsearchComponent: AlgoliaInstantsearchComponent

    public mobileFilterMenuIsOpen: boolean = false

    public visibilityScrollUpButton: boolean = false

    protected readonly ProductListFilterEnum = ProductListFilterEnum
    protected readonly AlgoliaFilter = AlgoliaFilter

    constructor(
        private route: ActivatedRoute,
        public productSelectionService: ProductSelectionService,
        public readonly authService: AuthService,
        public localization: LocalizationService,
        public debtorService: DebtorService,
        private analytics: AnalyticsService,
        private readonly destroy: DestroyRef,
        private router: Router,
    ) {
    }

    public ngOnInit(): void {
        this.clearRefinementsCommands$
            .pipe(takeUntilDestroyed(this.destroy))
            .subscribe(() => {
                this.instantsearchComponent.instantSearchInstance.helper?.clearRefinements()
                void this.router.navigate([], { relativeTo: this.route, queryParams: {} })
            })
    }

    public toggleFilterMenu(): void {
        this.mobileFilterMenuIsOpen = not(this.mobileFilterMenuIsOpen)
    }

    public toggleInSelectMode(): void {
        let isInSelectMode = this.productSelectionService.isInSelectMode$.getValue()
        this.productSelectionService.isInSelectMode$.next(! isInSelectMode)

        if (isInSelectMode) {
            this.productSelectionService.deselectAllProducts()
        }
    }

    public isIntersecting(status: boolean, name: string): void {
        this.visibilityScrollUpButton = ! status && name === 'header'
    }

    public scrollUp(): void {
        let scrollTarget = document.getElementById('header')

        if (scrollTarget)
            scrollTarget.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest',
            })
    }

    public productsLoaded(products: Product[]): void {
        const page = Number(this.route.snapshot.queryParams.page) || 1
        const locale = this.localization.getCurrentLocale()
        const pItems: Analytics.PItem[] = this.analytics.transformProductsToPItem(locale, products.slice(0, 20))
        this.analytics.logProductList(pItems, this.listType, page)
    }

}
